import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { getTopNiches30d, refreshTopNiches30d } from "@/lib/api";

function nFmt(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function fmtDuration(s) {
  if (!s || s <= 0) return "";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function TopNiches30d() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [facelessOnly, setFacelessOnly] = useState(false);
  const [englishOnly, setEnglishOnly] = useState(true);
  // Paginate 50 at a time. The full ranked list may have 50-120
  // niches depending on the pool size — user hits "Load 50 more"
  // to page through, resets on filter change.
  const [visibleCount, setVisibleCount] = useState(50);

  const load = useCallback(async () => {
    try {
      const d = await getTopNiches30d();
      setData(d);
      return d;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load top niches");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a scan is running so the page updates automatically
  // when it finishes. A full pool + classify pass takes ~60-90s;
  // 5s cadence is a reasonable trade-off between UX and DB load.
  useEffect(() => {
    if (!data) return undefined;
    const running = data.status === "running" || data.in_flight_status === "running";
    if (!running) return undefined;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [data, load]);

  const onRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const d = await refreshTopNiches30d(true);
      setData(d);
      if (d?.quota_exhausted) {
        toast.warning("YouTube quota exhausted — showing last cached results.");
      } else if (d?.status === "running" || d?.in_flight_status === "running") {
        toast.info("Scanning YouTube… this takes ~60-90 seconds. Results update automatically.");
      } else {
        toast.success(`Refreshed — ${(d?.niches || []).length} niches ranked from ${d?.pool_size || 0} videos.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const allNiches = data?.niches || [];
  let filteredNiches = allNiches;
  if (facelessOnly) filteredNiches = filteredNiches.filter((n) => n.is_faceless_dominant);
  if (englishOnly) filteredNiches = filteredNiches.filter((n) => (n.english_ratio ?? 100) >= 50);

  // Reset pagination whenever the filtered set changes size.
  useEffect(() => {
    setVisibleCount(50);
  }, [facelessOnly, englishOnly, filteredNiches.length]);

  const niches = filteredNiches.slice(0, visibleCount);
  const hasMore = filteredNiches.length > visibleCount;

  const facelessCount = allNiches.filter((n) => n.is_faceless_dominant).length;
  const ageH = data?.age_hours;
  const stale = data?.stale;
  const empty = !loading && filteredNiches.length === 0;

  return (
    <div
      data-testid="top-niches-30d-page"
      className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto"
    >
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 1 · TOP 50 NICHES · 30 DAYS
      </div>
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-none mb-3">
            The niches winning right now.
          </h1>
          <p className="text-zinc-300 text-sm max-w-2xl leading-relaxed">
            Ranks the 50 most-viewed YouTube niches over the last 30 days by
            total view volume across their top-performing videos. Click any
            row to see the exact videos and channels carrying that niche.
          </p>
          {data?.pool_size ? (
            <p className="text-zinc-500 text-xs mt-2 font-mono uppercase tracking-[0.25em]">
              · aggregated from {data.pool_size} videos
            </p>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <FilterChip
            label="faceless only"
            count={facelessCount}
            active={facelessOnly}
            onClick={() => setFacelessOnly((v) => !v)}
            testid="top-niches-30d-faceless-filter"
            title={
              facelessOnly
                ? "Showing only niches where 50%+ of channels are faceless"
                : "Show only faceless-dominant niches (50%+ of channels have no on-camera host)"
            }
          />
          <FilterChip
            label={englishOnly ? "english" : "all languages"}
            active={englishOnly}
            onClick={() => setEnglishOnly((v) => !v)}
            testid="top-niches-30d-language-filter"
            title={
              englishOnly
                ? "Only English-dominant niches. Click to include all languages."
                : "All languages. Click to filter to English-dominant niches."
            }
          />
          {ageH != null && (
            <span
              data-testid="top-niches-30d-cache-age"
              className={`font-mono text-[11px] uppercase tracking-[0.25em] ${
                stale ? "text-amber-400" : "text-zinc-500"
              }`}
            >
              cached {ageH < 1 ? `${Math.round(ageH * 60)}m` : `${ageH}h`} ago{stale ? " · stale" : ""}
            </span>
          )}
          <button
            data-testid="top-niches-30d-refresh-btn"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            {refreshing
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCcw className="w-3.5 h-3.5" />}
            {refreshing ? "refreshing…" : "refresh"}
          </button>
        </div>
      </div>

      {data?.quota_exhausted && (
        <div className="border border-amber-500/40 bg-amber-500/[0.06] rounded-sm px-4 py-3 mb-6">
          <div className="font-mono text-[10px] font-black uppercase tracking-[0.3em] text-amber-400 mb-1">
            {"// YOUTUBE QUOTA EXHAUSTED"}
          </div>
          <div className="text-[12px] text-amber-200">
            Showing the last cached results. Quota resets at midnight
            Pacific — or add another YOUTUBE_API_KEY_N to your .env.
          </div>
        </div>
      )}

      {(data?.status === "failed" || data?.in_flight_status === "failed") && (
        <div
          data-testid="top-niches-30d-failure-banner"
          className="border border-red-500/50 bg-red-500/[0.06] rounded-sm px-4 py-3 mb-6"
        >
          <div className="font-mono text-[10px] font-black uppercase tracking-[0.3em] text-red-400 mb-1">
            {"// SCAN FAILED"}
          </div>
          <div className="text-[12px] text-red-200">
            {data?.error === "scan_timed_out_or_pod_restarted"
              ? "The scan didn't finish — the server may have restarted mid-scan. Hit refresh to try again."
              : (data?.error || "Something went wrong. Hit refresh to retry.")}
          </div>
        </div>
      )}

      {(data?.status === "running" || data?.in_flight_status === "running") && (
        <div
          data-testid="top-niches-30d-running-banner"
          className="border border-yellow-400/60 bg-yellow-400/[0.06] rounded-sm px-4 py-3 mb-6 flex items-center gap-3"
        >
          <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
          <div>
            <div className="font-mono text-[10px] font-black uppercase tracking-[0.3em] text-yellow-400 mb-0.5">
              {"// SCANNING YOUTUBE"}
            </div>
            <div className="text-[12px] text-zinc-300">
              Pulling ~300 videos and classifying every channel. Takes
              about a minute — this page updates automatically when
              the scan finishes.
              {data.in_flight_status && data.niche_count > 0 ? (
                <> Showing the previous cached ranking below.</>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading top niches<span className="blink">_</span>
        </div>
      ) : refreshing && allNiches.length === 0 ? (
        <div className="border border-zinc-800 rounded-sm p-8 text-center text-zinc-400 text-sm">
          Running a fresh scan — this pulls ~300 videos and classifies them.
          Takes about 30 seconds.
        </div>
      ) : empty ? (
        <div className="border border-zinc-800 rounded-sm p-8 text-center text-zinc-400 text-sm">
          {facelessOnly || !englishOnly ? (
            <>
              No niches match the current filters. Try adjusting them or
              hit <b className="text-yellow-400">refresh</b>.
            </>
          ) : (
            <>
              No niches yet. Hit <b className="text-yellow-400">refresh</b> to
              pull the latest.
            </>
          )}
        </div>
      ) : (
        <div className="border border-zinc-900 rounded-sm overflow-hidden">
          <div className="grid grid-cols-[64px_1fr_150px_100px_140px_44px] gap-4 items-center px-4 py-3 border-b border-zinc-900 bg-zinc-950/60 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            <div>rank</div>
            <div>niche</div>
            <div className="text-right">total views</div>
            <div className="text-right">videos</div>
            <div className="text-right">faceless</div>
            <div />
          </div>
          {niches.map((n) => {
            const rank = allNiches.indexOf(n) + 1;
            const isOpen = expanded === n.niche;
            return (
              <NicheRow
                key={n.niche}
                niche={n}
                rank={rank}
                isOpen={isOpen}
                onToggle={() => setExpanded(isOpen ? null : n.niche)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, count, active, onClick, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      title={title}
      className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2.5 rounded-sm border transition-colors ${
        active
          ? "bg-yellow-400 text-black border-yellow-400"
          : "text-zinc-300 hover:text-yellow-400 border-zinc-800 hover:border-yellow-400"
      }`}
    >
      {label}
      {count != null && (
        <span className={`font-mono text-[10px] ${active ? "text-black/70" : "text-zinc-500"}`}>
          · {count}
        </span>
      )}
    </button>
  );
}

function NicheRow({ niche, rank, isOpen, onToggle }) {
  return (
    <div
      data-testid={`top-niche-row-${niche.niche.replace(/\s+/g, "-")}`}
      className={`border-b border-zinc-900 last:border-b-0 ${isOpen ? "bg-zinc-950/70" : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full grid grid-cols-[64px_1fr_150px_100px_140px_44px] gap-4 items-center px-4 py-4 text-left hover:bg-zinc-950/70 transition-colors"
      >
        <div className="font-mono text-xs font-black text-yellow-400">
          #{String(rank).padStart(2, "0")}
        </div>
        <div className="min-w-0">
          <div className="text-white text-sm font-bold truncate flex items-center gap-2">
            {niche.niche}
            {niche.is_faceless_dominant && (
              <span className="inline-flex items-center font-mono text-[9px] font-bold uppercase tracking-[0.18em] bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 px-1.5 py-0.5 rounded-sm">
                faceless
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 truncate mt-0.5">
            avg {nFmt(niche.avg_views)} views · {niche.channel_count} channel{niche.channel_count === 1 ? "" : "s"}
          </div>
        </div>
        <div className="text-right font-mono text-sm text-yellow-300 font-bold">
          {nFmt(niche.sum_views)}
        </div>
        <div className="text-right font-mono text-sm text-zinc-300">
          {niche.video_count}
        </div>
        <div className="text-right font-mono text-xs text-zinc-300">
          <span className={niche.is_faceless_dominant ? "text-emerald-300" : "text-zinc-400"}>
            {niche.faceless_pct.toFixed(0)}%
          </span>
          <span className="text-zinc-600 ml-1">
            ({niche.faceless_channel_count}/{niche.channel_count})
          </span>
        </div>
        <div className="flex items-center justify-center text-zinc-500">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {isOpen && <NicheDetail niche={niche} />}
    </div>
  );
}

function NicheDetail({ niche }) {
  return (
    <div className="px-6 pb-6 pt-2 space-y-5">
      {niche.top_channels?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
            {"// top channels in this niche"}
          </div>
          <div className="flex flex-wrap gap-2">
            {niche.top_channels.map((c) => (
              <a
                key={c.channel_id}
                href={`https://www.youtube.com/channel/${c.channel_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-zinc-800 hover:border-yellow-400 bg-zinc-950 px-3 py-1.5 rounded-sm text-[12px] text-zinc-200 hover:text-yellow-300 transition-colors"
              >
                <span className="font-bold truncate max-w-[240px]">{c.channel_title}</span>
                <span className="font-mono text-[10px] text-zinc-500">
                  {nFmt(c.views)} views · {c.videos}v
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
          {"// top videos in this niche"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {niche.top_videos.map((v) => (
            <a
              key={v.video_id}
              href={v.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block border border-zinc-900 hover:border-yellow-400/60 rounded-sm overflow-hidden bg-zinc-950/60 transition-colors"
            >
              <div className="relative aspect-video bg-zinc-900 overflow-hidden">
                {v.thumbnail_url && (
                  <img
                    src={v.thumbnail_url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                {v.duration_seconds > 0 && (
                  <div className="absolute bottom-2 right-2 font-mono text-[10px] bg-black/85 text-white px-1.5 py-0.5 rounded-sm">
                    {v.is_short ? "SHORT · " : ""}{fmtDuration(v.duration_seconds)}
                  </div>
                )}
              </div>
              <div className="p-3 space-y-1.5">
                <div className="text-[12px] font-bold text-white leading-snug line-clamp-2 group-hover:text-yellow-300">
                  {v.title}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {v.channel_title}
                </div>
                <div className="flex items-center gap-2 pt-0.5 font-mono text-[11px]">
                  <span className="text-yellow-400 font-bold">{nFmt(v.views)} views</span>
                  <ExternalLink className="w-3 h-3 text-zinc-600 ml-auto group-hover:text-yellow-400" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
